import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { BookOpen, DownloadSimple, MagnifyingGlass, X } from "@phosphor-icons/react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

const UPD_RE = /\s*\{updated:\s*(\d{4}-\d{2}-\d{2})\}\s*$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => { const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || ""); return p ? `${MONTHS[+p[2] - 1]} ${+p[3]}, ${p[1]}` : ""; };

const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (u && u.startsWith("/api") ? ASSET_BASE + u : u);
const escAttr = (s) => (s || "").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Minimal, safe markdown -> HTML (headings, bold, lists, tables, hr, paragraphs).
const mdToHtml = (md, fallbackDate) => {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");
  const lines = (md || "").split("\n");
  let html = "", listType = null, inTable = false;
  const closeList = () => { if (listType) { html += listType === "ol" ? "</ol>" : "</ul>"; listType = null; } };
  const openList = (type, cls) => { if (listType !== type) { closeList(); html += `<${type} class="${cls}">`; listType = type; } };
  const closeTable = () => { if (inTable) { html += "</tbody></table>"; inTable = false; } };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (/^\|(.+)\|$/.test(line)) {
      const cells = line.split("|").slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue;
      if (!inTable) { closeList(); html += '<table class="w-full text-sm border-collapse my-3"><tbody>'; inTable = true; }
      const tag = "td";
      html += "<tr>" + cells.map((c) => `<${tag} class="border border-border px-2 py-1 align-top">${inline(c)}</${tag}>`).join("") + "</tr>";
      continue;
    } else { closeTable(); }
    if (/^### /.test(line)) { closeList(); const t = line.slice(4).replace(UPD_RE, ""); html += `<h3 id="sec-${slugify(t)}" class="font-semibold text-base mt-4 mb-1 scroll-mt-16">${inline(t)}</h3>`; }
    else if (/^## /.test(line)) {
      closeList();
      const raw = line.slice(3); const um = raw.match(UPD_RE); const t = raw.replace(UPD_RE, "");
      const d = (um && um[1]) || fallbackDate;
      html += `<h2 id="sec-${slugify(t)}" class="font-display font-bold text-lg mt-6 mb-1 border-b border-border pb-1 scroll-mt-16">${inline(t)}</h2>`;
      if (d && t.trim().toLowerCase() !== "table of contents") html += `<div class="text-[11px] text-muted-foreground italic mb-2">Updated ${fmtDate(d)}</div>`;
    }
    else if (/^# /.test(line)) { closeList(); const t = line.slice(2).replace(UPD_RE, ""); html += `<h1 id="sec-${slugify(t)}" class="font-display font-black text-2xl mt-2 mb-3 scroll-mt-16">${inline(t)}</h1>`; }
    else if (/^> /.test(line)) { closeList(); html += `<blockquote class="border-l-2 border-primary/40 bg-secondary/40 pl-3 py-1 my-2 text-sm">${inline(line.slice(2))}</blockquote>`; }
    else if (/^---+$/.test(line)) { closeList(); html += '<hr class="my-4 border-border" />'; }
    else if (/^!\[[^\]]*\]\([^)]+\)\s*$/.test(line)) { closeList(); const mm = line.match(/^!\[([^\]]*)\]\(([^)]+)\)/); html += `<img class="block max-w-full h-auto my-3 border border-border rounded-sm shadow-sm" src="${escAttr(resolveAsset(mm[2]))}" alt="${escAttr(mm[1])}" loading="lazy"/>`; }
    else if (/^\s*\d+\. /.test(line)) { openList("ol", "list-decimal pl-5 space-y-0.5 my-2"); html += `<li>${inline(line.replace(/^\s*\d+\. /, ""))}</li>`; }
    else if (/^\s*[-*] /.test(line)) { openList("ul", "list-disc pl-5 space-y-0.5 my-2"); html += `<li>${inline(line.replace(/^\s*[-*] /, ""))}</li>`; }
    else if (line.trim() === "") { closeList(); }
    else { closeList(); html += `<p class="my-2 text-sm leading-relaxed">${inline(line)}</p>`; }
  }
  closeList(); closeTable();
  return html;
};

export default function ConsoleManuals() {
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [pendingSlug, setPendingSlug] = useState(null);

  useEffect(() => {
    api.get("/guides/catalog").then(({ data }) => {
      setCatalog(data);
      if (data.length) select(data[0].key);
    }).catch(() => toast.error("Couldn't load the manuals catalog."));
  }, []);

  // debounced cross-manual search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/guides/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // scroll to a section once the selected manual has rendered
  useEffect(() => {
    if (doc && pendingSlug) {
      const el = document.getElementById(`sec-${pendingSlug}`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); el.classList.add("bg-yellow-100"); setTimeout(() => el.classList.remove("bg-yellow-100"), 1800); }
      setPendingSlug(null);
    }
  }, [doc, pendingSlug]);

  const select = (key, slug = null) => {
    setActive(key); setLoading(true); setDoc(null);
    if (slug) setPendingSlug(slug);
    api.get(`/guides/admin/${key}`).then(({ data }) => setDoc(data))
      .catch(() => toast.error("Couldn't open that manual."))
      .finally(() => setLoading(false));
  };

  const openResult = (r) => { setResults(null); setQuery(""); select(r.key, slugify(r.heading)); };

  const download = async (key) => {
    try {
      const res = await api.get(`/guides/${key}/pdf`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `${key}_manual.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Download failed."); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="console-manuals-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-primary/10 border border-primary/20"><BookOpen className="text-primary" size={22} /></div>
        <div>
          <h1 className="text-2xl font-display font-bold">Manuals</h1>
          <p className="text-sm text-muted-foreground">Every ClaroSuite manual — read or download any of them. Customers only ever see the manual matching their plan.</p>
        </div>
      </div>

      <div className="relative mt-3 max-w-xl" data-testid="manual-search">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all manuals — jump to any topic…"
          className="rounded-none pl-9 pr-9" data-testid="manual-search-input" />
        {query && <button onClick={() => { setQuery(""); setResults(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="manual-search-clear"><X size={16} /></button>}
        {results !== null && query.trim().length >= 2 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-border shadow-lg max-h-80 overflow-y-auto" data-testid="manual-search-results">
            {results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">No matches.</div>
            ) : results.map((r, i) => (
              <button key={i} onClick={() => openResult(r)} data-testid={`manual-search-result-${i}`}
                className="w-full text-left px-3 py-2 border-b border-border last:border-0 hover:bg-secondary/50">
                <div className="text-[11px] uppercase tracking-wide text-primary font-semibold">{r.title} · {r.heading}</div>
                {r.snippet && <div className="text-xs text-muted-foreground truncate">{r.snippet}</div>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-5 mt-4">
        <aside className="space-y-1">
          {catalog.map((g) => (
            <button key={g.key} onClick={() => select(g.key)} data-testid={`manual-item-${g.key}`}
              className={`w-full text-left border px-3 py-2 text-sm transition-colors ${active === g.key ? "bg-white border-primary font-semibold" : "border-border hover:border-primary/50"}`}>
              <div className="truncate">{g.title}</div>
              <div className="text-[11px] text-muted-foreground">{g.audience}</div>
            </button>
          ))}
        </aside>

        <main className="border border-border bg-white min-h-[400px]">
          {loading || !doc ? (
            <p className="text-sm text-muted-foreground p-6">{loading ? "Loading…" : "Select a manual."}</p>
          ) : (
            <div>
              <div className="flex items-center justify-between border-b border-border px-5 py-3 sticky top-0 bg-white">
                <span className="font-semibold text-sm truncate" data-testid="manual-title">{doc.title}</span>
                <Button size="sm" className="rounded-none" onClick={() => download(doc.key)} data-testid="manual-download-btn"><DownloadSimple size={15} className="mr-1.5" /> PDF</Button>
              </div>
              <div className="px-6 py-4 prose-sm max-w-none" data-testid="manual-body"
                dangerouslySetInnerHTML={{ __html: mdToHtml(doc.markdown, doc.last_updated) }} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
